import {
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Divider,
  Badge,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { KeyboardArrowLeft } from "@mui/icons-material";
import DynamicFeedOutlinedIcon from "@mui/icons-material/DynamicFeedOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import UserAvatar from "../common/UserAvatar";
import { selectCurrentUser } from "../pages/auth/authSlice";
import { useGetChatConversationsQuery } from "../../app/communicationApiSlice";

function CommunicationSideNav({ handleDrawerToggle }) {
  const theme = useTheme();
  const contrastText = theme.palette.primary.contrastText;
  const user = useSelector(selectCurrentUser);

  const userRoles = user?.roles?.map((r) => r?.role?.name).filter(Boolean) || [];
  const isAdmin = userRoles.includes("admin");
  const isTeacher = userRoles.includes("teacher");
  const isParent = userRoles.includes("parent");

  const roleLabel = isAdmin
    ? "Admin"
    : isTeacher
    ? "Teacher"
    : isParent
    ? "Parent"
    : "Staff";

  const { data: convsData } = useGetChatConversationsQuery();
  const totalUnread = (convsData?.conversations || []).reduce(
    (acc, c) => acc + (c.unread_count || 0),
    0
  );

  const navItems = [
    {
      text: "Feed",
      link: "/communication/feed",
      muiIcon: <DynamicFeedOutlinedIcon sx={{ fontSize: "20px", color: "inherit" }} />,
    },
    {
      text: "Messages",
      link: "/communication/messages",
      muiIcon: (
        <Badge badgeContent={totalUnread} color="error" max={99}>
          <ChatBubbleOutlineIcon sx={{ fontSize: "20px", color: "inherit" }} />
        </Badge>
      ),
    },
    {
      text: "Notifications",
      link: "/communication/settings",
      muiIcon: <NotificationsNoneOutlinedIcon sx={{ fontSize: "20px", color: "inherit" }} />,
    },
  ];

  const displayName =
    `${user?.profile?.first_name || user?.first_name || ""} ${user?.profile?.last_name || user?.last_name || ""}`.trim() ||
    user?.username ||
    "User";

  return (
    <Box sx={{ mx: 3, display: "flex", flexDirection: "column", height: "100%" }}>
      {/* User profile section */}
      <Box
        sx={{
          margin: "2em 0 3em 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            width: "100%",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <UserAvatar
              src={user?.photo || user?.profile?.photo}
              name={displayName}
              sx={{
                width: 80,
                height: 80,
                border: `3px solid ${alpha(contrastText, 0.8)}`,
                bgcolor: alpha(contrastText, 0.2),
                color: contrastText,
              }}
            />
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: contrastText,
                  textTransform: "capitalize",
                  lineHeight: 1.2,
                }}
              >
                {displayName}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: "rgba(0,0,0,0.6)",
                  textTransform: "capitalize",
                  backgroundColor: "rgba(255,255,255,0.85)",
                  borderRadius: "4px",
                  px: 1,
                  py: 0.25,
                  lineHeight: 1.8,
                }}
              >
                {roleLabel}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              display: { xs: "block", md: "none" },
              color: contrastText,
              position: "absolute",
              right: 0,
            }}
          >
            <KeyboardArrowLeft sx={{ color: "#95969D" }} />
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ backgroundColor: alpha(contrastText, 0.2), mb: 2 }} />

      {/* Navigation */}
      <List
        sx={{
          flex: 1,
          "& .MuiTypography-root": {
            fontSize: "16px",
            fontWeight: 700,
            lineHeight: "25px",
            color: "inherit",
          },
          "& a": {
            color: contrastText,
            textDecoration: "none",
            borderRadius: "8px",
            width: "100%",
          },
        }}
      >
        {navItems.map((item, index) => (
          <ListItem key={index} disablePadding sx={{ margin: "5px 0" }}>
            <Box
              component={NavLink}
              to={item.link}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                padding: "12px",
                borderRadius: "8px",
                textDecoration: "none",
                color: "inherit",
                "&.active": { backgroundColor: alpha(contrastText, 0.23) },
                "&:hover": { backgroundColor: alpha(contrastText, 0.15) },
                transition: "background-color 0.3s ease",
              }}
              onClick={handleDrawerToggle}
            >
              <Box
                sx={{
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {item.muiIcon}
              </Box>
              <ListItemText primary={item.text} />
            </Box>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

export default CommunicationSideNav;
